import { useState } from 'react';
import { Trash2, X, Film } from 'lucide-react';
import { fileAuthUrl, isImageAttachment } from './AttachmentGallery';

export const isVideoAttachment = (contentType) => (contentType || '').startsWith('video/');

// Inline photos/videos that belong to the task description (always visible, not buried in attachments).
export function DescriptionMedia({ media = [], canDelete = false, onDelete }) {
  const [preview, setPreview] = useState(null);
  if (media.length === 0) return null;

  return (
    <div className="space-y-2 mt-2" data-testid="description-media">
      {media.map(m => (
        <div key={m.id} className="relative group rounded-md overflow-hidden border border-[#E0E0E0] bg-[#101010]">
          {isVideoAttachment(m.content_type) ? (
            <video
              src={fileAuthUrl(m.storage_path)}
              controls
              preload="metadata"
              className="w-full max-h-[340px] bg-black"
              data-testid={`description-video-${m.id}`}
            />
          ) : isImageAttachment(m.content_type) ? (
            <button
              type="button"
              onClick={() => setPreview(m)}
              className="block w-full"
              title={m.filename}
              data-testid={`description-image-${m.id}`}
            >
              <img src={fileAuthUrl(m.storage_path)} alt={m.filename} className="w-full max-h-[340px] object-contain bg-[#F5F5F5]" loading="lazy" />
            </button>
          ) : (
            <a href={fileAuthUrl(m.storage_path)} className="block p-3 text-sm text-white truncate">{m.filename}</a>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={() => onDelete(m.id)}
              className="absolute top-2 right-2 p-1.5 rounded bg-white/90 text-[#666666] hover:text-[#DC2626] opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`delete-description-media-${m.id}`}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ))}

      {preview && (
        <div
          className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="description-media-lightbox"
        >
          <img
            src={fileAuthUrl(preview.storage_path)}
            alt={preview.filename}
            className="max-h-[88vh] max-w-full object-contain rounded"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={() => setPreview(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 text-white hover:bg-white/30"
          >
            <X size={22} />
          </button>
        </div>
      )}
    </div>
  );
}

// Compact preview strip for task cards so media is noticed at a glance.
export function DescriptionMediaStrip({ media = [] }) {
  if (!media || media.length === 0) return null;
  const items = media.slice(0, 3);
  return (
    <div className="flex items-center gap-1.5 mt-2" data-testid="description-media-strip">
      {items.map(m => (
        <div key={m.id} className="w-12 h-12 rounded border border-[#E0E0E0] overflow-hidden bg-[#F5F5F5] flex items-center justify-center relative">
          {isVideoAttachment(m.content_type) ? (
            <Film size={16} className="text-[#666666]" />
          ) : (
            <img src={fileAuthUrl(m.storage_path)} alt={m.filename} className="w-full h-full object-cover" loading="lazy" />
          )}
        </div>
      ))}
      {media.length > 3 && (
        <span className="text-[11px] text-[#999999]">+{media.length - 3}</span>
      )}
    </div>
  );
}
